import { create } from 'zustand';
import { clientService } from '@/services/clientService';
import type { Client, ClientInsert } from '@/types/client';

interface ClientStore {
  clients: Client[];
  isLoading: boolean;
  error: string | null;

  fetchClients: () => Promise<void>;
  addClient: (client: ClientInsert) => Promise<Client | null>;
  updateClient: (id: string, updates: Partial<ClientInsert>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
}

export const useClientStore = create<ClientStore>((set) => ({
  clients: [],
  isLoading: false,
  error: null,

  fetchClients: async () => {
    set({ isLoading: true, error: null });
    try {
      const clients = await clientService.getClients();
      set({ clients, isLoading: false });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      set({ error: message, isLoading: false });
    }
  },

  addClient: async (clientData) => {
    set({ isLoading: true, error: null });
    try {
      const newClient = await clientService.createClient(clientData);
      if (newClient) {
        set(state => ({
          clients: [newClient, ...state.clients],
          isLoading: false
        }));
      }
      return newClient;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateClient: async (id, updates) => {
    set({ isLoading: true, error: null });
    try {
      const updatedClient = await clientService.updateClient(id, updates);
      if (updatedClient) {
        set(state => ({
          clients: state.clients.map(c => c.id === id ? updatedClient : c),
          isLoading: false
        }));
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      set({ error: message, isLoading: false });
    }
  },

  deleteClient: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await clientService.deleteClient(id);
      set(state => ({
        clients: state.clients.filter(c => c.id !== id),
        isLoading: false
      }));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      set({ error: message, isLoading: false });
    }
  }
}));
